import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'

// Elements
import ParseContent from 'components/shared/ParseContent'

// Images
import ArrowRight from 'img/arrowright.inline.svg'

const Content = styled(ParseContent)`
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin-bottom: 1.75rem;
    @media (min-width: 992px) {
      font-size: 48px;
    }
  }
`

const Item = styled.div`
  background-color: ${({ theme }) => theme.color.grey};

  & h4 {
    font-family: ${({ theme }) => theme.font.family.heading};
    font-weight: 500;
    font-size: 22px;
    margin-bottom: 0;
    @media (min-width: 992px) {
      font-size: 28px;
    }
  }

  & svg {
    width: 22px;
    height: 18px;
  }
`

const Header = styled.div`
  cursor: pointer;
`

const ArrowWrapper = styled.div<{ open: boolean }>`
  min-width: 22px;
  & svg path {
    ${(props) =>
      props.open &&
      css`
        fill: ${({ theme }) => theme.color.primary};
      `}
  }
`

interface FAQProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_Faq
}

const FAQ: React.FC<FAQProps> = ({ fields }) => {
  const [currentIndex, setCurrentIndex] = useState<number | null>(0)

  const arrowVariants = {
    open: {
      rotate: 90,
    },
    collapsed: {
      rotate: 0,
    },
  }

  const answerVariants = {
    open: {
      opacity: 1,
      height: 'auto',
      overflow: 'hidden',
    },
    collapsed: {
      opacity: 0,
      height: 0,
      overflow: 'hidden',
    },
  }

  return (
    <section className="mb-5" id={fields.sectionid}>
      <div className="container py-lg-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            {fields.description && (
              <Content content={fields.description} className="mb-4" />
            )}
            {fields.items?.map((item, index) => {
              const open: boolean = currentIndex === index

              return (
                // eslint-disable-next-line react/no-array-index-key
                <Item key={index} className="p-md-4 p-3 mb-3">
                  <Header
                    className="d-flex align-items-center justify-content-between"
                    onClick={() =>
                      open ? setCurrentIndex(null) : setCurrentIndex(index)
                    }
                    role="button"
                  >
                    <h4>{item?.question}</h4>
                    <ArrowWrapper className="ms-3" open={open}>
                      <motion.div
                        initial="collapsed"
                        animate={open ? 'open' : 'collapsed'}
                        variants={arrowVariants}
                      >
                        <ArrowRight />
                      </motion.div>
                    </ArrowWrapper>
                  </Header>
                  <AnimatePresence>
                    {open && (
                      <motion.div
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={answerVariants}
                        transition={{
                          duration: 0.8,
                          ease: [0.04, 0.62, 0.23, 0.98],
                        }}
                      >
                        <ParseContent content={item?.answer} className="pt-3" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Item>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default FAQ
